import { useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, X } from "lucide-react";
import { completeOnboarding } from "../api/api";

const VncModal = ({ vncDetails, internalAccountId, closeModal, refreshAccounts }) => {
  const [loading, setLoading] = useState(true);

  // Build noVNC URL through the proxy on the SAME origin as the frontend.
  // In production, nginx's ^~ /novnc/ location proxies to the MT5 VM.
  // In dev, the Vite novnc-proxy plugin does the same.
  const port = vncDetails.novnc_port;
  const hostSegment = vncDetails.novnc_host ? `${encodeURIComponent(vncDetails.novnc_host)}/` : "";
  const origin = window.location.origin;
  const query = new URLSearchParams({
    autoconnect: "true",
    resize: "scale",
    shared: "true",
    password: vncDetails.vnc_password || "",
    path: "websockify",
  });
  const vncUrl = `${origin}/novnc/${hostSegment}${port}/vnc.html?${query.toString()}`;

  const handleLoginSuccess = async () => {
    try {
      await completeOnboarding(internalAccountId);
      refreshAccounts();
      closeModal();
    } catch (err) {
      console.error("Error completing onboarding:", err);
      alert("Failed to complete onboarding.");
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[2000] flex h-[100dvh] w-[100dvw] flex-col bg-backgroundSurface"
      role="dialog"
      aria-modal="true"
      aria-label="MetaTrader Session"
    >
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex shrink-0 items-center justify-between border-b border-borderDefault px-4 py-3">
          <h3 className="text-xl font-semibold text-textPrimary">MetaTrader Session</h3>
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-textSecondary transition hover:bg-backgroundInput hover:text-destructive focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Close MetaTrader"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="relative min-h-0 flex-1 overflow-hidden bg-black">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-textSecondary text-sm">Connecting to MetaTrader session...</p>
                <p className="text-textSecondary text-xs mt-1">MT5 terminal may take a moment to appear</p>
              </div>
            </div>
          )}
          <iframe
            src={vncUrl}
            className="h-full w-full border-0"
            title="MetaTrader Session"
            allow="clipboard-read; clipboard-write; fullscreen"
            allowFullScreen
            onLoad={() => setLoading(false)}
          />
        </div>
        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-borderDefault px-4 py-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-borderDefault bg-backgroundInput px-4 text-sm font-medium text-textPrimary transition hover:border-primary/40 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Close MetaTrader
          </button>
          <button
            type="button"
            onClick={handleLoginSuccess}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-textAccent shadow-sm transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-backgroundSurface"
          >
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
            Complete MT5 Login
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modal;
  }

  return createPortal(modal, document.body);
};

export default VncModal;
